import { Link } from 'react-router-dom';
import { KeyRound, Plus, Share, Smartphone } from 'lucide-react';
import { Button, Card, CardMeta, CardTitle, Logo, StripeBand } from '@/design/components';
import { useSettings } from '@/access/SettingsContext';
import { detectPlatform, isStandalone } from './platform';
import { useInstallPrompt } from './useInstallPrompt';

/**
 * מסך ההתקנה (PRD 5.16). הקישור מהודעת ברוכים הבאים מגיע לכאן.
 * אנדרואיד: כפתור שמפעיל את חלון ההתקנה של הדפדפן.
 * אייפון: שלושה צעדים. בדפדפן שאינו Safari: הנחיה לפתוח ב Safari.
 */
export function InstallPage() {
  const { brand } = useSettings();
  const platform = detectPlatform();
  const { canPrompt, promptInstall } = useInstallPrompt();
  const installed = isStandalone();

  return (
    <div className="grid min-h-dvh grid-rows-[auto_1fr] bg-cream">
      <div className="bg-charcoal">
        <div className="flex h-32 items-center justify-center">
          <Logo on="charcoal" height={36} />
        </div>
        <StripeBand />
      </div>

      <main className="mx-auto grid w-full max-w-md content-start gap-6 p-4 pt-8">
        <div className="grid gap-2">
          <h1 className="m-0 font-display text-display-l font-semibold">
            התקינו את האפליקציה
          </h1>
          <p className="m-0 text-body text-muted">
            {installed
              ? 'האפליקציה מותקנת. אפשר להיכנס.'
              : `אחרי ההתקנה ${brand.app_title} תיפתח מהמסך הראשי, כמו כל אפליקציה.`}
          </p>
        </div>

        {!installed && platform === 'android' && (
          <Card>
            <CardTitle>אנדרואיד</CardTitle>
            <CardMeta>לחיצה אחת, והאייקון יתווסף למסך הבית.</CardMeta>
            <Button
              variant="primary"
              block
              disabled={!canPrompt}
              onClick={() => void promptInstall()}
              className="mt-2"
            >
              {canPrompt ? 'התקן' : 'פתח/י את התפריט ובחר/י "התקנת אפליקציה"'}
            </Button>
          </Card>
        )}

        {!installed && platform === 'ios-safari' && (
          <Card>
            <CardTitle>אייפון</CardTitle>
            <ol className="m-0 grid list-none gap-3 p-0">
              <Step index={1} icon={<Share size={24} strokeWidth={1.75} aria-hidden="true" />}>
                בתחתית Safari, לחצו על כפתור השיתוף.
              </Step>
              <Step index={2} icon={<Plus size={24} strokeWidth={1.75} aria-hidden="true" />}>
                גללו ובחרו "הוסף למסך הבית".
              </Step>
              <Step
                index={3}
                icon={<Smartphone size={24} strokeWidth={1.75} aria-hidden="true" />}
              >
                לחצו "הוסף". האייקון יופיע במסך הבית.
              </Step>
            </ol>
          </Card>
        )}

        {!installed && platform === 'ios-other' && (
          <Card>
            <CardTitle>פתחו את הקישור ב Safari</CardTitle>
            <CardMeta>
              באייפון אפשר להוסיף אפליקציה למסך הבית רק מ Safari. העתיקו את הכתובת, פתחו Safari
              והדביקו אותה שם.
            </CardMeta>
          </Card>
        )}

        {!installed && platform === 'desktop' && (
          <Card>
            <CardTitle>פתחו את הקישור בטלפון</CardTitle>
            <CardMeta>
              האפליקציה בנויה לטלפון. שלחו לעצמכם את הקישור, או סרקו אותו מהמחשב.
            </CardMeta>
          </Card>
        )}

        {/*
          הכניסה מוסברת כאן ולא רק בדף ההתחברות: ההודעה שמפנה
          לכאן היא הפנייה הראשונה של המערכת לעובד, ו"אין סיסמה"
          הוא בדיוק מה שהוא מחפש ולא מוצא.
        */}
        <Card>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <KeyRound size={20} strokeWidth={1.75} aria-hidden="true" />
              איך נכנסים
            </span>
          </CardTitle>
          <CardMeta>
            אין סיסמה. מקלידים את מספר הטלפון שרשום אצלנו, מקבלים קוד בן שש ספרות ב SMS,
            ומזינים אותו. זהו.
          </CardMeta>
        </Card>

        <Link
          to="/login"
          className="min-h-11 content-center text-center text-body font-medium text-ink underline"
        >
          {installed ? 'כניסה' : 'המשך בלי להתקין'}
        </Link>
      </main>
    </div>
  );
}

function Step({
  index,
  icon,
  children,
}: {
  index: number;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-3">
      <span className="grid size-11 shrink-0 place-items-center rounded-pill bg-olive-soft text-ink">
        {icon}
      </span>
      <span className="text-body">
        <span className="font-medium">{index}. </span>
        {children}
      </span>
    </li>
  );
}
